import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import fingerprintService from '../../../services/fingerprintService';
import NotificationToast from '../../../components/ui/NotificationToast';

const FingerprintEnrollmentModal = ({ isOpen, onClose, employee, onEnrolled }) => {
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1 = left hand, 2 = right hand, 3 = complete
  const [enrolledHands, setEnrolledHands] = useState({
    left: false,
    right: false
  });

  useEffect(() => {
    if (isOpen && employee) {
      checkDeviceAndEnrollment();
    }
  }, [isOpen, employee]);

  const checkDeviceAndEnrollment = async () => {
    try {
      // Check device status
      const device = await fingerprintService.getDeviceStatus();
      setDeviceStatus(device);

      // Check existing enrollment
      if (employee?.id) {
        const enrollment = await fingerprintService.checkEnrollment(employee.id);
        if (enrollment?.success) {
          setEnrollmentStatus(enrollment.data);
          // Note: Backend should return which hands are enrolled
        }
      }
    } catch (error) {
      console.error('Error checking device/enrollment:', error);
    }
  };

  const handleEnroll = async (hand = 'left') => {
    if (!deviceStatus?.available) {
      setNotification({
        message: 'Fingerprint device is not available. Please ensure RD Service is running.',
        type: 'error'
      });
      return;
    }

    if (!employee?.id) {
      setNotification({
        message: 'Employee information is required',
        type: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      setCapturing(true);
      setNotification({
        message: `Please place your ${hand === 'left' ? 'left' : 'right'} hand finger on the scanner...`,
        type: 'info'
      });

      // Capture fingerprint
      const captureResult = await fingerprintService.captureFingerprint();
      
      if (!captureResult.success) {
        throw new Error(captureResult.error || 'Failed to capture fingerprint');
      }

      setNotification({
        message: 'Fingerprint captured. Enrolling...',
        type: 'info'
      });

      // Enroll fingerprint
      const enrollResult = await fingerprintService.enrollFingerprint(
        employee.id,
        captureResult.template,
        hand
      );

      if (enrollResult?.success) {
        setEnrolledHands(prev => ({ ...prev, [hand]: true }));
        setNotification({
          message: `${hand === 'left' ? 'Left' : 'Right'} hand fingerprint enrolled successfully!`,
          type: 'success'
        });

        // Move to next step or complete
        if (hand === 'left' && !enrolledHands.right) {
          setCurrentStep(2);
          setNotification({
            message: 'Left hand enrolled! Now enroll your right hand (optional).',
            type: 'success'
          });
        } else {
          setCurrentStep(3);
        }

        // Refresh enrollment status
        await checkDeviceAndEnrollment();
        onEnrolled?.();
      } else {
        throw new Error(enrollResult.error || 'Failed to enroll fingerprint');
      }
    } catch (error) {
      setNotification({
        message: error.message || 'Failed to enroll fingerprint. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setCapturing(false);
    }
  };

  const handleSkipRight = () => {
    setCurrentStep(3);
    setNotification({
      message: 'Enrollment complete! You can enroll right hand later if needed.',
      type: 'info'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="ScanLine" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Fingerprint Enrollment</h2>
              <p className="text-sm text-muted-foreground">
                {employee?.full_name || 'Employee'} - {employee?.employee_id || ''}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={loading || capturing}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <NotificationToast
            message={notification?.message}
            type={notification?.type}
            onDismiss={() => setNotification(null)}
          />

          {/* Device Status */}
          <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon 
                name={deviceStatus?.available ? "CheckCircle" : "AlertCircle"} 
                size={20} 
                className={deviceStatus?.available ? "text-green-600" : "text-red-600"} 
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {deviceStatus?.available ? 'Device Ready' : 'Device Not Available'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {deviceStatus?.message || 'Checking device...'}
                </p>
              </div>
            </div>
          </div>

          {/* Enrollment Steps */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Icon name="ScanLine" size={32} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Enroll Left Hand Fingerprint
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Place your left hand finger on the scanner. This will be your primary fingerprint for attendance.
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="Info" size={16} />
                  Instructions
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Place your left hand finger flat on the scanner</li>
                  <li>Center the finger pad on the sensor</li>
                  <li>Apply gentle, even pressure</li>
                  <li>Keep finger still during capture (2-3 seconds)</li>
                  <li>Ensure finger is clean and dry</li>
                </ul>
              </div>

              <Button
                onClick={() => handleEnroll('left')}
                disabled={loading || capturing || !deviceStatus?.available}
                className="w-full flex items-center justify-center gap-2"
                size="lg"
              >
                {capturing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Capturing Fingerprint...
                  </>
                ) : (
                  <>
                    <Icon name="ScanLine" size={20} />
                    Enroll Left Hand Fingerprint
                  </>
                )}
              </Button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                  <Icon name="CheckCircle" size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Left Hand Enrolled Successfully!
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Now enroll your right hand fingerprint (optional but recommended for better accuracy).
                </p>
              </div>

              <Button
                onClick={() => handleEnroll('right')}
                disabled={loading || capturing || !deviceStatus?.available}
                className="w-full flex items-center justify-center gap-2"
                size="lg"
              >
                {capturing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Capturing Fingerprint...
                  </>
                ) : (
                  <>
                    <Icon name="ScanLine" size={20} />
                    Enroll Right Hand Fingerprint (Optional)
                  </>
                )}
              </Button>

              <Button
                onClick={handleSkipRight}
                variant="outline"
                className="w-full"
                disabled={loading || capturing}
              >
                Skip - Enroll Right Hand Later
              </Button>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Enrollment Complete!
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your fingerprint has been enrolled successfully. You can now use fingerprint attendance.
              </p>

              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm font-medium text-foreground mb-2">Enrolled Hands:</p>
                <div className="flex gap-4 justify-center">
                  <div className="flex items-center gap-2">
                    <Icon name={enrolledHands.left ? "CheckCircle" : "XCircle"} 
                      size={16} 
                      className={enrolledHands.left ? "text-green-600" : "text-gray-400"} 
                    />
                    <span className="text-sm">Left Hand {enrolledHands.left ? '✓' : '✗'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name={enrolledHands.right ? "CheckCircle" : "XCircle"} 
                      size={16} 
                      className={enrolledHands.right ? "text-green-600" : "text-gray-400"} 
                    />
                    <span className="text-sm">Right Hand {enrolledHands.right ? '✓' : '✗'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-6 border-t border-border px-6 pb-6">
          {currentStep === 3 ? (
            <Button onClick={onClose} className="flex items-center gap-2">
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading || capturing}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FingerprintEnrollmentModal;

